'use client';

/**
 * Client-Side Error Reporting Module
 *
 * ⚠️ CRITICAL: This file MUST have 'use client' directive at the top!
 * Without it, importing from this module in any component causes the entire
 * module tree (including Sentry) to be evaluated during SSR, which triggers
 * crypto.randomUUID() errors during Next.js 16 PPR.
 *
 * This module provides all client-side error reporting functionality:
 * - Breadcrumb collection (automatic + manual)
 * - Error capture and reporting
 * - React integration (hook + provider)
 *
 * @example
 * ```tsx
 * // In your root layout
 * import { ErrorReportingProvider } from '@/lib/error-reporting/client';
 *
 * // In components
 * import { useErrorReporting, addBreadcrumb } from '@/lib/error-reporting/client';
 * ```
 */

// =============================================================================
// PROVIDER (Use in root layout)
// =============================================================================

export {
  ErrorReportingProvider,
  useErrorReportingContext,
} from './ErrorReportingProvider';

// =============================================================================
// USER SYNC (Use in dashboard layout to sync auth data to error reports)
// =============================================================================

export { ErrorReportingUserSync } from './ErrorReportingUserSync';

// =============================================================================
// REACT HOOK (Use in components)
// =============================================================================

export { useErrorReporting } from './use-error-reporting';

// =============================================================================
// BREADCRUMB FUNCTIONS (For manual tracking)
// =============================================================================

export {
  addBreadcrumb,
  getBreadcrumbs,
  clearBreadcrumbs,
  trackFormSubmission,
  trackFormFieldChange,
  trackModalOpen,
  trackModalClose,
  trackSlowOperation,
  getSessionDuration,
  getViewportInfo,
  getMemoryInfo,
  initAutoBreadcrumbs,
  // 🆕 Filter tracking (for debugging filter-related UX issues)
  trackFilterApply,
  trackFilterClear,
  trackFilterSearch,
  trackFilterNavigation,
  trackFilterIssue,
} from './breadcrumbs';

// =============================================================================
// ERROR CAPTURE (For manual error reporting)
// =============================================================================

export {
  captureClientError,
  captureComponentError,
  captureUserReport,
  installGlobalErrorHandlers,
} from './reporter';

// =============================================================================
// USER CONTEXT (Set when user logs in)
// =============================================================================

export {
  setUserContext,
  getUserContext,
  clearUserContext,
} from './reporter';
